import { useEffect, useState, useMemo } from 'react'

import { ReservationErrors } from '@enums'
import { ApiError } from '@lib/api'
import { MaxAttemptsError, PollingApiError, ShopperError } from '@lib/polling'

export type ReservationErrorType =
  | 'booking'
  | 'createError'
  | 'confirmError'
  | 'shopper'
  | 'promocode'
  | 'alreadyConfirmed'

interface HookResult {
  errorType: ReservationErrorType | null
  errorProp: Error | undefined | null
  closeModal: () => void
}

interface Errors {
  reservationCreate: ApiError | null
  quickReservationCreate: ApiError | null
  pollingError: PollingApiError | null
  confirmError: ApiError | null
}

interface HookProps {
  errors: Errors
  paymentAttempts: number
  isLoading: boolean
}

const includedErrors = ['createError', 'shopper', 'confirmError']

const PAYMENT_ATTEMPTS = 2

const isPromocodeError = (error: ApiError): boolean => {
  const ERROR_CODE = '600.400.001'
  return Array.isArray(error?.data?.errors) && error.data.errors.some(e => e.code === ERROR_CODE)
}

const getErrorType = (errors: Errors, paymentAttempts: number): ReservationErrorType | null => {
  const { reservationCreate, quickReservationCreate, confirmError, pollingError } = errors

  if (reservationCreate) return 'createError'
  if (quickReservationCreate) {
    if (isPromocodeError(quickReservationCreate)) return 'promocode'
    return 'booking'
  }
  if (confirmError) {
    if (confirmError.message === ReservationErrors.Confirmed) return 'alreadyConfirmed'
    if (paymentAttempts === PAYMENT_ATTEMPTS) return 'booking'
    return 'confirmError'
  }
  if (pollingError) {
    if (pollingError instanceof ShopperError) return 'shopper'
    if (!(pollingError instanceof MaxAttemptsError)) return 'booking'
  }

  return null
}

const useReservationErrors: (props: HookProps) => HookResult = props => {
  const { errors, paymentAttempts, isLoading } = props
  const [errorType, setErrorType] = useState<ReservationErrorType | null>(null)

  useEffect(() => {
    if (isLoading) return

    setErrorType(getErrorType(errors, paymentAttempts))
  }, [errors, paymentAttempts, isLoading])

  const errorProp = useMemo<Error | undefined | null>(() => {
    const { quickReservationCreate, confirmError, pollingError } = errors
    const error = quickReservationCreate ?? confirmError ?? pollingError

    if (error instanceof ShopperError) return error
    if (error instanceof MaxAttemptsError) return error
    if (errorType && includedErrors.includes(errorType)) return error

    return null
  }, [errors, errorType])

  return {
    errorType,
    errorProp,
    closeModal: () => setErrorType(null),
  }
}

export default useReservationErrors
